// Courier Invoices page — uploads, audits, and emails credit-note
// requests for parcel-carrier invoices. Tabs:
//   • Invoices     — upload + checker + flag review + credit-email
//   • POD Reports  — upload proof-of-delivery reports used by the checker
//   • Rates        — courier rate cards (price-check source of truth)
//   • Couriers     — courier registry (name, credit-note email, active)
//
// Currently only DHL has audit rules implemented; the UI is courier-
// agnostic so adding a new courier is just inserting a row + rate card.

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { FileDropzone } from "@/components/file-dropzone";
import { useToast } from "@/hooks/use-toast";
import {
  Truck, Loader2, Trash2, FileText, Upload, ClipboardCheck, Mail,
  AlertTriangle, CheckCircle2, Tag, Plus, Settings, RefreshCw, XCircle
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { useAuth } from "@/hooks/use-auth";
import { CollectionFeesSection } from "@/components/courier-invoices/collection-fees";
import { CollectionChecksSection, supportsCollectionChecks } from "@/components/courier-invoices/collection-checks";
import { isRoyalMail } from "@/lib/royal-mail";
import { getGetCourierCollectionFeesQueryKey, getGetCourierCollectionChecksQueryKey, useGetCourierCollectionChecks } from "@workspace/api-client-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type Tab = "invoices" | "pod" | "rates" | "couriers";

type SurchargeType = "percent" | "per_order" | null;

interface Courier {
  id: number;
  code: string;
  name: string;
  creditEmail: string | null;
  longLengthSurcharge: number | null;
  fuelSurchargeType: SurchargeType;
  fuelSurchargeValue: number | null;
  hgvSurchargeType: SurchargeType;
  hgvSurchargeValue: number | null;
  active: boolean;
  notes: string | null;
}

/** Mirror of server-side `creditDue` — see routes/courierInvoices.ts. */
function creditDue(expected: number | null, actual: number | null, kind?: string): number {
  if (kind === "third_party_collection" && expected == null) return 0;
  if (expected != null && actual != null) return Math.max(0, actual - expected);
  return Math.max(0, actual ?? 0);
}

function formatSurcharge(type: SurchargeType, value: number | null): string {
  if (!type || value == null) return "—";
  if (type === "percent") return `${value}%`;
  return `£${value.toFixed(2)}/order`;
}

interface Rate {
  id: number;
  courierId: number;
  service: string;
  zone: string | null;
  zoneLabel: string | null;
  firstParcel: number | null;
  subsequentParcel: number | null;
  additionalCharge: number | null;
  longLengthSurcharge: number | null;
}

interface ReconciliationService {
  invoiceQuantity: number;
  trackerQuantity: number;
  unknownMethods: string[];
}

interface ReconciliationDate {
  date: string;
  status: "complete" | "warning";
  services: Record<string, ReconciliationService>;
  trackerUniqueRoyalMailBarcodes: number | null;
  workflowUniqueRoyalMailBarcodes: number | null;
  missingFromTracker: string[];
  missingFromManifest: string[];
  warnings: string[];
}

interface ReconciliationResult {
  generatedAt: string;
  status: "complete" | "warning";
  dates: ReconciliationDate[];
}

interface InvoiceUpload {
  id: number;
  courierId: number;
  courierName: string | null;
  courierCode: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  originalFilename: string;
  lineCount: number;
  totalValue: number;
  checkedAt: string | null;
  flagCount: number;
  flagTotalValue: number;
  creditEmailSentAt: string | null;
  reconciliationResult?: ReconciliationResult | null;
  reconciliationAt?: string | null;
  createdAt: string;
}

interface PodUpload {
  id: number;
  courierId: number;
  courierName: string | null;
  originalFilename: string;
  rowCount: number;
  dateFrom: string | null;
  dateTo: string | null;
  createdAt: string;
}

interface InvoiceLine {
  id: number;
  rowIndex: number;
  consignmentNumber: string | null;
  customerReference: string | null;
  reference: string | null;
  jobDate: string | null;
  serviceDesc: string | null;
  zone: string | null;
  items: number | null;
  weight: number | null;
  value: number | null;
  longLengthCharge: number | null;
  heavyWeightSurcharge: number | null;
  outerPostcode: string | null;
  innerPostcode: string | null;
}

interface Flag {
  id: number;
  invoiceLineId: number;
  kind: "price" | "no_rate" | "weight" | "long_length" | "pod" | "fuel" | "hgv" | "overweight" | "third_party_collection";
  reason: string;
  expected: number | null;
  actual: number | null;
}

interface InvoiceDetail {
  upload: InvoiceUpload;
  courier: Courier | null;
  lines: InvoiceLine[];
  flags: Flag[];
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}/api${path}`, {
    credentials: "include",
    headers: init?.body && !(init.body instanceof FormData) ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try { const j = await res.json(); msg = j.error || msg; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

export default function CourierInvoicesPage() {
  const [tab, setTab] = useState<Tab>("invoices");
  const [selectedCourierId, setSelectedCourierId] = useState<number | null>(null);

  const couriers = useQuery({
    queryKey: ["courier-invoices", "couriers"],
    queryFn: () => api<Courier[]>("/courier-invoices/couriers"),
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-duffells-grey flex items-center gap-3">
              <Truck className="w-8 h-8 text-duffells-orange" /> Courier Invoices
            </h1>
            <p className="text-duffells-grey/70 mt-1">
              Upload courier invoices and POD reports, run the audit, and request credit notes for over-charges.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-duffells-grey/70">Courier</label>
            <select
              className="border border-slate-200 rounded px-2 py-1 text-sm"
              value={selectedCourierId ?? ""}
              onChange={(e) => setSelectedCourierId(Number(e.target.value) || null)}
            >
              <option value="">Select a courier…</option>
              {(couriers.data || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
              ))}
            </select>
          </div>
        </header>

        <nav className="flex gap-1 border-b border-slate-200">
          {([
            ["invoices", "Invoices", FileText],
            ["pod", "POD Reports", ClipboardCheck],
            ["rates", "Rate Card", Tag],
            ["couriers", "Couriers", Settings],
          ] as [Tab, string, any][]).map(([k, label, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                tab === k
                  ? "border-duffells-orange text-duffells-orange"
                  : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>

        {tab === "invoices" && (
          <InvoicesTab courierId={selectedCourierId} couriers={couriers.data || []} />
        )}
        {tab === "pod" && (
          <PodTab courierId={selectedCourierId} couriers={couriers.data || []} />
        )}
        {tab === "rates" && (
          <RatesTab courierId={selectedCourierId} />
        )}
        {tab === "couriers" && (
          <CouriersTab couriers={couriers.data || []} />
        )}
      </div>
    </Layout>
  );
}

// ============================================================
// Invoices tab
// ============================================================

function InvoicesTab({ courierId, couriers }: { courierId: number | null; couriers: Courier[] }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<number | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const list = useQuery({
    queryKey: ["courier-invoices", "uploads"],
    queryFn: () => api<InvoiceUpload[]>("/courier-invoices/invoices"),
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      if (!courierId) throw new Error("Pick a courier first");
      const fd = new FormData();
      fd.append("file", files[0]!);
      fd.append("courierId", String(courierId));
      return api<InvoiceUpload>("/courier-invoices/invoices/upload", { method: "POST", body: fd });
    },
    onSuccess: (u) => {
      toast({ title: "Invoice uploaded", description: `${u.lineCount} lines, £${(u.totalValue || 0).toFixed(2)}` });
      qc.invalidateQueries({ queryKey: ["courier-invoices", "uploads"] });
      setOpenId(u.id);
    },
    onError: (err: any) => toast({ title: "Upload failed", description: err.message, variant: "destructive" }),
  });

  const del = useMutation({
    mutationFn: (id: number) => api(`/courier-invoices/invoices/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["courier-invoices", "uploads"] }),
  });

  const filtered = useMemo(() => {
    const all = list.data || [];
    return courierId ? all.filter((u) => u.courierId === courierId) : all;
  }, [list.data, courierId]);

  const selectedCourier = couriers.find(c => c.id === courierId);
  const isRm = isRoyalMail(selectedCourier?.code);

  return (
    <section className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <Upload className="w-4 h-4" /> Upload courier invoice
        </h2>
        <FileDropzone
          accept=".pdf,.xlsx,.xls"
          maxSize={25 * 1024 * 1024}
          label="Drop the courier invoice (.pdf / .xlsx / .xls)"
          onUpload={async (files) => { await upload.mutateAsync(files); }}
        />
        {!courierId && <p className="text-xs text-amber-600 mt-2">Select a courier first.</p>}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/60">
            <tr>
              <th className="px-4 py-2 text-left">Filename</th>
              <th className="px-4 py-2 text-left">Invoice #</th>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-right">Lines</th>
              <th className="px-4 py-2 text-right">Total</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Flags</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr><td colSpan={8} className="p-8 text-center text-duffells-grey/50"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading…</td></tr>
            )}
            {!list.isLoading && filtered.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-duffells-grey/50">No invoices yet.</td></tr>
            )}
            {filtered.map((u) => {
              const uIsRm = isRoyalMail(u.courierCode);
              return (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-xs">{u.originalFilename}</td>
                  <td className="px-4 py-2">{u.invoiceNumber || "—"}</td>
                  <td className="px-4 py-2">{u.invoiceDate ? format(parseISO(u.invoiceDate), "dd MMM yyyy") : "—"}</td>
                  <td className="px-4 py-2 text-right">{u.lineCount}</td>
                  <td className="px-4 py-2 text-right">£{(u.totalValue || 0).toFixed(2)}</td>
                  <td className="px-4 py-2">
                    {uIsRm ? (
                      u.reconciliationAt
                        ? (u.reconciliationResult?.status === "warning"
                          ? <span className="inline-flex items-center gap-1 text-amber-700 text-xs"><AlertTriangle className="w-3 h-3" /> Reconciled {format(parseISO(u.reconciliationAt), "dd MMM HH:mm")}</span>
                          : <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="w-3 h-3" /> Reconciled {format(parseISO(u.reconciliationAt), "dd MMM HH:mm")}</span>)
                        : <span className="text-duffells-grey/50 text-xs">Not reconciled</span>
                    ) : (
                      <>
                        {u.checkedAt
                          ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="w-3 h-3" /> Checked {format(parseISO(u.checkedAt), "dd MMM HH:mm")}</span>
                          : <span className="text-duffells-grey/50 text-xs">Not checked</span>}
                        {u.creditEmailSentAt && <div className="text-xs text-blue-700 mt-0.5"><Mail className="w-3 h-3 inline" /> Sent {format(parseISO(u.creditEmailSentAt), "dd MMM HH:mm")}</div>}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {uIsRm ? (
                      u.reconciliationAt ? (
                        u.reconciliationResult?.status === "warning"
                          ? <span className="text-amber-700 text-xs font-medium">Warnings</span>
                          : <span className="text-emerald-700 text-xs">Clean</span>
                      ) : <span className="text-duffells-grey/40 text-xs">—</span>
                    ) : (
                      u.flagCount > 0
                        ? <span className="inline-flex items-center gap-1 text-amber-700 text-xs"><AlertTriangle className="w-3 h-3" /> {u.flagCount} · £{(u.flagTotalValue || 0).toFixed(2)}</span>
                        : u.checkedAt ? <span className="text-emerald-700 text-xs">All clean</span> : <span className="text-duffells-grey/40 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setOpenId(u.id)}>Open</Button>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => {
                        if (confirm(`Delete invoice ${u.originalFilename}?`)) del.mutate(u.id);
                      }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openId && <InvoiceDrawer id={openId} couriers={couriers} onClose={() => setOpenId(null)} />}
    </section>
  );
}

function InvoiceDrawer({ id, couriers, onClose }: { id: number; couriers: Courier[]; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const detail = useQuery({
    queryKey: ["courier-invoices", "detail", id],
    queryFn: () => api<InvoiceDetail>(`/courier-invoices/invoices/${id}`),
  });

  const check = useMutation({
    mutationFn: () => api<{ ok: boolean; flagCount: number; warning: string | null }>(
      `/courier-invoices/invoices/${id}/check`, { method: "POST" }
    ),
    onSuccess: (r) => {
      // Checking may automatically discover servicing-locality postcodes.
      // Refresh the generated settings query as well as invoice results.
      if (detail.data?.upload.courierId) qc.invalidateQueries({ queryKey: getGetCourierCollectionFeesQueryKey(detail.data.upload.courierId) });
      toast({
        title: `Audit complete — ${r.flagCount} flag(s)`,
        description: r.warning || undefined,
        variant: r.warning ? "destructive" : "default",
      });
      qc.invalidateQueries({ queryKey: ["courier-invoices"] });
      qc.invalidateQueries({ queryKey: getGetCourierCollectionChecksQueryKey(id) });
    },
    onError: (err: any) => toast({ title: "Audit failed", description: err.message, variant: "destructive" }),
  });

  const reconcile = useMutation({
    mutationFn: () => api<{ ok: boolean; reconciliation: ReconciliationResult }>(
      `/courier-invoices/invoices/${id}/reconcile`, { method: "POST" }
    ),
    onSuccess: (r) => {
      toast({
        title: `Reconciliation complete`,
        description: r.reconciliation.status === "warning" ? "Warnings found during reconciliation" : "All clean",
        variant: r.reconciliation.status === "warning" ? "destructive" : "default",
      });
      qc.invalidateQueries({ queryKey: ["courier-invoices"] });
    },
    onError: (err: any) => toast({ title: "Reconciliation failed", description: err.message, variant: "destructive" }),
  });

  const sendEmail = useMutation({
    mutationFn: (override: string | null) => api<{ ok: boolean; sentTo: string; lineCount: number; totalDisputed: number }>(
      `/courier-invoices/invoices/${id}/email-credit-request`,
      { method: "POST", body: JSON.stringify(override ? { to: override } : {}) },
    ),
    onSuccess: (r) => {
      toast({ title: "Credit-note request sent", description: `${r.lineCount} line(s) totalling £${r.totalDisputed.toFixed(2)} → ${r.sentTo}` });
      qc.invalidateQueries({ queryKey: ["courier-invoices"] });
    },
    onError: (err: any) => toast({ title: "Email failed", description: err.message, variant: "destructive" }),
  });

  const collectionSupported = supportsCollectionChecks(detail.data?.courier?.code);
  // Shares the cache entry with CollectionChecksSection (same key), so no duplicate state.
  const collectionReport = useGetCourierCollectionChecks(id, {
    query: { queryKey: getGetCourierCollectionChecksQueryKey(id), enabled: collectionSupported },
  });
  const auditStale = collectionSupported && !!collectionReport.data?.stale;

  const linesById = useMemo(() => {
    const m = new Map<number, InvoiceLine>();
    for (const l of detail.data?.lines || []) m.set(l.id, l);
    return m;
  }, [detail.data]);

  const courier = detail.data?.courier || null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold">
              {detail.data?.upload.invoiceNumber || `Invoice #${id}`}
            </h2>
            <p className="text-sm text-duffells-grey/60">
              {detail.data?.upload.originalFilename} · {courier?.name || "Courier"}
              {detail.data?.upload.invoiceDate && ` · ${format(parseISO(detail.data.upload.invoiceDate), "dd MMM yyyy")}`}
            </p>
          </div>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>

        <div className="p-5 space-y-5">
          {isRoyalMail(courier?.code) ? (
            <RmReconciliationView
              detail={detail.data!}
              reconcile={reconcile}
            />
          ) : (
            <>
              <div className="flex gap-2">
                <Button onClick={() => check.mutate()} disabled={check.isPending}>
                  {check.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}
                  {detail.data?.upload.checkedAt ? "Re-run audit" : "Run audit"}
                </Button>
                <Button
                  variant="outline"
                  disabled={!detail.data || detail.data.flags.length === 0 || sendEmail.isPending || auditStale}
                  title={auditStale ? "Lookup corrections changed since the last audit. Re-run the audit before sending." : undefined}
                  data-testid="button-send-credit-email"
                  onClick={() => {
                    const fallback = courier?.creditEmail || "";
                    const to = prompt(
                      fallback ? `Send credit-note request to:` : `No credit email configured. Enter recipient:`,
                      fallback,
                    );
                    if (!to) return;
                    sendEmail.mutate(to === fallback ? null : to);
                  }}
                >
                  {sendEmail.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send credit-note request email
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-3 text-sm">
                <Stat label="Lines" value={String(detail.data?.upload.lineCount ?? "—")} />
                <Stat label="Invoice total" value={`£${(detail.data?.upload.totalValue ?? 0).toFixed(2)}`} />
                <Stat label="Disputed" value={`£${(detail.data?.upload.flagTotalValue ?? 0).toFixed(2)}`} tone={detail.data?.upload.flagCount ? "warn" : "muted"} />
              </div>

              <section>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" /> Flags ({detail.data?.flags.length ?? 0})
                </h3>
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/60">
                      <tr>
                        <th className="px-3 py-2 text-left">Reason</th>
                        <th className="px-3 py-2 text-left">Detail</th>
                        <th className="px-3 py-2 text-left">Customer ref</th>
                        <th className="px-3 py-2 text-left">Tracking</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-right" title="Difference between what was invoiced and what should have been (the amount to claim as credit). For under-charges this is what we'd owe back; for over-charges, what the courier owes us.">Credit due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.data?.flags ?? []).length === 0 && (
                        <tr><td colSpan={6} className="p-6 text-center text-duffells-grey/50">
                          {detail.data?.upload.checkedAt ? "No discrepancies found." : "Audit not run yet."}
                        </td></tr>
                      )}
                      {(detail.data?.flags ?? []).map((f) => {
                        const l = linesById.get(f.invoiceLineId);
                        return (
                          <tr key={f.id} className="border-t border-slate-100">
                            <td className="px-3 py-2"><KindBadge kind={f.kind} /></td>
                            <td className="px-3 py-2 text-xs">{f.reason}</td>
                            <td className="px-3 py-2 text-xs font-mono">{l?.customerReference || "—"}</td>
                            <td className="px-3 py-2 text-xs font-mono">{l?.consignmentNumber || "—"}</td>
                            <td className="px-3 py-2 text-xs">{l?.jobDate ? format(parseISO(l.jobDate), "dd MMM") : "—"}</td>
                            <td className="px-3 py-2 text-right">{f.kind === "third_party_collection" && f.expected == null ? "Review" : `£${creditDue(f.expected, f.actual, f.kind).toFixed(2)}`}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              {supportsCollectionChecks(courier?.code) && (
                <CollectionChecksSection key={id} invoiceId={id} isAdmin={isAdmin} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "warn" | "muted" }) {
  const colour = tone === "warn" ? "text-amber-700" : tone === "muted" ? "text-duffells-grey/60" : "text-duffells-grey";
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="text-xs uppercase text-duffells-grey/50">{label}</div>
      <div className={`text-lg font-semibold ${colour}`}>{value}</div>
    </div>
  );
}

function KindBadge({ kind }: { kind: Flag["kind"] }) {
  const styles: Record<Flag["kind"], string> = {
    price: "bg-rose-100 text-rose-700",
    no_rate: "bg-slate-100 text-slate-700",
    weight: "bg-amber-100 text-amber-700",
    long_length: "bg-violet-100 text-violet-700",
    pod: "bg-blue-100 text-blue-700",
    fuel: "bg-orange-100 text-orange-700",
    hgv: "bg-teal-100 text-teal-700",
    overweight: "bg-yellow-100 text-yellow-800",
    third_party_collection: "bg-fuchsia-100 text-fuchsia-700",
  };
  const labels: Record<Flag["kind"], string> = {
    price: "Price",
    no_rate: "No rate",
    weight: "Heavy wt",
    long_length: "Long length",
    pod: "POD failure",
    fuel: "Fuel",
    hgv: "HGV",
    overweight: "Overweight £/kg",
    third_party_collection: "3rd-party collection",
  };
  return <span className={`text-xs px-2 py-0.5 rounded font-medium ${styles[kind]}`}>{labels[kind]}</span>;
}

function RmReconciliationView({
  detail,
  reconcile
}: {
  detail: InvoiceDetail;
  reconcile: ReturnType<typeof useMutation<any, any, void>>;
}) {
  const result = detail.upload.reconciliationResult;

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <Button onClick={() => reconcile.mutate()} disabled={reconcile.isPending}>
          {reconcile.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {detail.upload.reconciliationAt ? "Re-run reconciliation" : "Run reconciliation"}
        </Button>
      </div>

      {result ? (
        <div className="space-y-6">
          {result.dates.map((d, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="bg-slate-50 p-4 border-b border-slate-200 flex justify-between items-center">
                <h3 className="font-bold text-lg text-slate-800">{format(parseISO(d.date), "dd MMMM yyyy")}</h3>
                {d.status === "warning" ? (
                  <span className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                    <AlertTriangle className="w-4 h-4" /> Issues found
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                    <CheckCircle2 className="w-4 h-4" /> Clean
                  </span>
                )}
              </div>

              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-sm mb-3 text-slate-500 uppercase tracking-wide">Service Breakdown</h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wide">
                        <th className="text-left font-semibold pb-2">Service</th>
                        <th className="text-right font-semibold pb-2">Invoiced</th>
                        <th className="text-right font-semibold pb-2">Tracker</th>
                        <th className="text-right font-semibold pb-2">Diff</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {Object.entries(d.services || {}).map(([service, s]) => {
                        const hasInv = s.invoiceQuantity != null;
                        const hasTrk = s.trackerQuantity != null;
                        const diff = hasInv && hasTrk ? s.invoiceQuantity - s.trackerQuantity : null;
                        return (
                          <tr key={service}>
                            <td className="py-3 font-medium text-slate-800">{service}</td>
                            <td className="py-3 text-right font-mono text-slate-700">{hasInv ? s.invoiceQuantity : "—"}</td>
                            <td className="py-3 text-right font-mono text-slate-700">{hasTrk ? s.trackerQuantity : "—"}</td>
                            <td className="py-3 text-right">
                              {diff !== null ? (
                                diff !== 0 ? (
                                  <span className="text-amber-600 font-bold bg-amber-50 px-1.5 py-0.5 rounded">
                                    {diff > 0 ? `+${diff}` : diff}
                                  </span>
                                ) : (
                                  <span className="text-emerald-600 font-bold bg-emerald-50 px-1.5 py-0.5 rounded">0</span>
                                )
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {Object.keys(d.services || {}).length === 0 && (
                        <tr>
                          <td colSpan={4} className="py-4 text-center text-slate-400 italic">No services billed for this date.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div>
                  <h4 className="font-semibold text-sm mb-3 text-slate-500 uppercase tracking-wide">Daily Manifest Reach</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-slate-50 border border-slate-100 p-3 rounded-lg">
                      <span className="text-slate-600 text-sm font-medium">Tracker Barcodes</span>
                      <span className="font-mono text-slate-800 font-semibold">{d.trackerUniqueRoyalMailBarcodes != null ? d.trackerUniqueRoyalMailBarcodes : "—"}</span>
                    </div>
                    <div className="flex justify-between items-center bg-slate-50 border border-slate-100 p-3 rounded-lg">
                      <span className="text-slate-600 text-sm font-medium">Workflow Barcodes</span>
                      <span className="font-mono text-slate-800 font-semibold">{d.workflowUniqueRoyalMailBarcodes != null ? d.workflowUniqueRoyalMailBarcodes : "—"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {d.warnings && d.warnings.length > 0 && (
                <div className="bg-amber-50 border-t border-amber-100 p-4">
                  <h4 className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4" /> Mismatches & Warnings
                  </h4>
                  <ul className="list-disc pl-5 space-y-1.5 text-sm text-amber-700">
                    {d.warnings.map((w, idx) => (
                      <li key={idx}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center p-12 bg-slate-50 rounded-xl border border-slate-200 border-dashed">
          <p className="text-slate-500 font-medium">Run reconciliation to match billed quantities against dispatched parcels.</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// POD tab
// ============================================================

function PodTab({ courierId, couriers }: { courierId: number | null; couriers: Courier[] }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const list = useQuery({
    queryKey: ["courier-invoices", "pod"],
    queryFn: () => api<PodUpload[]>("/courier-invoices/pod-reports"),
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      if (!courierId) throw new Error("Pick a courier first");
      const fd = new FormData();
      fd.append("file", files[0]!);
      fd.append("courierId", String(courierId));
      return api<PodUpload>("/courier-invoices/pod-reports/upload", { method: "POST", body: fd });
    },
    onSuccess: (u) => {
      toast({ title: "POD report uploaded", description: `${u.rowCount} rows` });
      qc.invalidateQueries({ queryKey: ["courier-invoices", "pod"] });
    },
    onError: (err: any) => toast({ title: "Upload failed", description: err.message, variant: "destructive" }),
  });

  const del = useMutation({
    mutationFn: (id: number) => api(`/courier-invoices/pod-reports/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["courier-invoices", "pod"] }),
  });

  const filtered = useMemo(() => {
    const all = list.data || [];
    return courierId ? all.filter((u) => u.courierId === courierId) : all;
  }, [list.data, courierId]);

  return (
    <section className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <Upload className="w-4 h-4" /> Upload POD report
        </h2>
        <FileDropzone
          accept=".xlsx,.xls"
          maxSize={25 * 1024 * 1024}
          label="Drop the POD report (.xls / .xlsx)"
          onUpload={async (files) => { await upload.mutateAsync(files); }}
        />
        <p className="text-xs text-duffells-grey/60 mt-2">
          POD rows are matched into the invoice audit by tracking number — and where that's missing, by customer reference.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/60">
            <tr>
              <th className="px-4 py-2 text-left">Filename</th>
              <th className="px-4 py-2 text-right">Rows</th>
              <th className="px-4 py-2 text-left">Date range</th>
              <th className="px-4 py-2 text-left">Uploaded</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={5} className="p-8 text-center"><Loader2 className="w-4 h-4 inline animate-spin" /></td></tr>}
            {!list.isLoading && filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-duffells-grey/50">No POD reports yet.</td></tr>}
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-mono text-xs">{u.originalFilename}</td>
                <td className="px-4 py-2 text-right">{u.rowCount}</td>
                <td className="px-4 py-2 text-xs">
                  {u.dateFrom && u.dateTo
                    ? `${format(parseISO(u.dateFrom), "dd MMM")} – ${format(parseISO(u.dateTo), "dd MMM yyyy")}`
                    : "—"}
                </td>
                <td className="px-4 py-2 text-xs">{format(parseISO(u.createdAt), "dd MMM HH:mm")}</td>
                <td className="px-4 py-2 text-right">
                  {isAdmin && (
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Delete POD report ${u.originalFilename}?`)) del.mutate(u.id); }}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ============================================================
// Rates tab
// ============================================================

function RatesTab({ courierId }: { courierId: number | null }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [showAdd, setShowAdd] = useState(false);

  const rates = useQuery({
    queryKey: ["courier-invoices", "rates", courierId],
    queryFn: () => api<Rate[]>(`/courier-invoices/couriers/${courierId}/rates`),
    enabled: !!courierId,
  });

  const save = useMutation({
    mutationFn: (r: Partial<Rate> & { id?: number }) => {
      if (r.id) return api(`/courier-invoices/rates/${r.id}`, { method: "PATCH", body: JSON.stringify(r) });
      return api(`/courier-invoices/couriers/${courierId}/rates`, { method: "POST", body: JSON.stringify(r) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["courier-invoices", "rates", courierId] });
      setShowAdd(false);
    },
    onError: (err: any) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const del = useMutation({
    mutationFn: (id: number) => api(`/courier-invoices/rates/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["courier-invoices", "rates", courierId] }),
  });

  if (!courierId) return <p className="text-duffells-grey/50">Select a courier.</p>;

  return (
    <section className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setShowAdd((s) => !s)}>
            <Plus className="w-4 h-4 mr-1" /> Add rate
          </Button>
        </div>
      )}
      {showAdd && (
        <RateForm onSubmit={(r) => save.mutate(r)} onCancel={() => setShowAdd(false)} />
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/60">
            <tr>
              <th className="px-4 py-2 text-left">Service</th>
              <th className="px-4 py-2 text-left">Zone</th>
              <th className="px-4 py-2 text-left">Region</th>
              <th className="px-4 py-2 text-right">First parcel</th>
              <th className="px-4 py-2 text-right">Subsequent</th>
              <th className="px-4 py-2 text-right">Per-consignment</th>
              <th className="px-4 py-2 text-right">Long length / item</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rates.isLoading && <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="w-4 h-4 inline animate-spin" /></td></tr>}
            {(rates.data || []).map((r) => (
              <RateRow
                key={r.id}
                rate={r}
                onSave={(patch) => save.mutate({ ...patch, id: r.id })}
                onDelete={isAdmin ? () => { if (confirm(`Delete rate row?`)) del.mutate(r.id); } : null}
                canEdit={isAdmin}
              />
            ))}
          </tbody>
        </table>
      </div>
      <CollectionFeesSection key={courierId} courierId={courierId} isAdmin={isAdmin} />
    </section>
  );
}

function RateRow({ rate, onSave, onDelete, canEdit }: {
  rate: Rate;
  onSave: (patch: Partial<Rate>) => void;
  onDelete: (() => void) | null;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <tr className="border-t border-slate-100">
        <td className="px-4 py-2">{rate.service}</td>
        <td className="px-4 py-2">{rate.zone || "—"}</td>
        <td className="px-4 py-2 text-xs text-duffells-grey/60">{rate.zoneLabel || "—"}</td>
        <td className="px-4 py-2 text-right">£{(rate.firstParcel ?? 0).toFixed(2)}</td>
        <td className="px-4 py-2 text-right">{rate.subsequentParcel == null ? "—" : `£${rate.subsequentParcel.toFixed(2)}`}</td>
        <td className="px-4 py-2 text-right">{rate.additionalCharge == null ? "—" : `£${rate.additionalCharge.toFixed(2)}`}</td>
        <td className="px-4 py-2 text-right">{rate.longLengthSurcharge == null ? "—" : `£${rate.longLengthSurcharge.toFixed(2)}`}</td>
        <td className="px-4 py-2 text-right space-x-1">
          {canEdit && <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Button>}
          {onDelete && <Button size="sm" variant="ghost" onClick={onDelete}><Trash2 className="w-3 h-3" /></Button>}
        </td>
      </tr>
    );
  }
  return (
    <RateFormInline
      initial={rate}
      onSubmit={(patch) => { onSave(patch); setEditing(false); }}
      onCancel={() => setEditing(false)}
    />
  );
}

function RateForm({ onSubmit, onCancel }: { onSubmit: (r: Partial<Rate>) => void; onCancel: () => void }) {
  return (
    <RateFormInline
      initial={{ service: "", zone: "", zoneLabel: "", firstParcel: 0, subsequentParcel: null, additionalCharge: null, longLengthSurcharge: null }}
      onSubmit={onSubmit}
      onCancel={onCancel}
      asCard
    />
  );
}

function RateFormInline({ initial, onSubmit, onCancel, asCard }: {
  initial: Partial<Rate>;
  onSubmit: (r: Partial<Rate>) => void;
  onCancel: () => void;
  asCard?: boolean;
}) {
  const [r, setR] = useState({
    service: initial.service ?? "",
    zone: initial.zone ?? "",
    zoneLabel: initial.zoneLabel ?? "",
    firstParcel: initial.firstParcel ?? 0,
    subsequentParcel: initial.subsequentParcel ?? null,
    additionalCharge: initial.additionalCharge ?? null,
    longLengthSurcharge: initial.longLengthSurcharge ?? null,
  });
  const num = (v: string) => v === "" ? null : Number(v);
  const inputs = (
    <>
      <input className="border rounded px-2 py-1 w-full" placeholder="Service" value={r.service} onChange={(e) => setR({ ...r, service: e.target.value })} />
      <input className="border rounded px-2 py-1 w-full" placeholder="Zone (A)" value={r.zone || ""} onChange={(e) => setR({ ...r, zone: e.target.value.toUpperCase() })} />
      <input className="border rounded px-2 py-1 w-full text-xs" placeholder="Region label" value={r.zoneLabel || ""} onChange={(e) => setR({ ...r, zoneLabel: e.target.value })} />
      <input type="number" step="0.01" className="border rounded px-2 py-1 w-24 text-right" value={r.firstParcel ?? ""} onChange={(e) => setR({ ...r, firstParcel: Number(e.target.value) })} />
      <input type="number" step="0.01" className="border rounded px-2 py-1 w-24 text-right" value={r.subsequentParcel ?? ""} onChange={(e) => setR({ ...r, subsequentParcel: num(e.target.value) })} />
      <input type="number" step="0.01" className="border rounded px-2 py-1 w-24 text-right" value={r.additionalCharge ?? ""} onChange={(e) => setR({ ...r, additionalCharge: num(e.target.value) })} />
      <input type="number" step="0.01" className="border rounded px-2 py-1 w-24 text-right" value={r.longLengthSurcharge ?? ""} onChange={(e) => setR({ ...r, longLengthSurcharge: num(e.target.value) })} />
    </>
  );
  if (asCard) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-7 gap-2">{inputs}</div>
        <div className="mt-3 flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={() => onSubmit(r)}>Save</Button>
        </div>
      </div>
    );
  }
  // Inline edit: collapse the whole row into a single cell that spans
  // every column to keep <tr><td> structure valid.
  return (
    <tr className="border-t border-slate-100 bg-amber-50/40">
      <td colSpan={8} className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          {inputs}
          <Button size="sm" onClick={() => onSubmit(r)}>Save</Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </td>
    </tr>
  );
}

// ============================================================
// Couriers tab
// ============================================================

function CouriersTab({ couriers }: { couriers: Courier[] }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [adding, setAdding] = useState(false);
  const emptyDraft = {
    code: "", name: "", creditEmail: "", longLengthSurcharge: "",
    fuelSurchargeType: "" as "" | "percent" | "per_order",
    fuelSurchargeValue: "",
    hgvSurchargeType: "" as "" | "percent" | "per_order",
    hgvSurchargeValue: "",
    notes: "",
  };
  const [draft, setDraft] = useState(emptyDraft);

  const save = useMutation({
    mutationFn: (c: any) => {
      if (c.id) return api(`/courier-invoices/couriers/${c.id}`, { method: "PATCH", body: JSON.stringify(c) });
      return api(`/courier-invoices/couriers`, { method: "POST", body: JSON.stringify(c) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["courier-invoices", "couriers"] });
      setAdding(false);
      setDraft(emptyDraft);
    },
    onError: (err: any) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  return (
    <section className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setAdding((s) => !s)}>
            <Plus className="w-4 h-4 mr-1" /> Add courier
          </Button>
        </div>
      )}
      {adding && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="grid grid-cols-4 gap-2">
            <input className="border rounded px-2 py-1" placeholder="Code (e.g. DHL)" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
            <input className="border rounded px-2 py-1" placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <input className="border rounded px-2 py-1 col-span-2" placeholder="Credit email" value={draft.creditEmail} onChange={(e) => setDraft({ ...draft, creditEmail: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SurchargeField
              label="Long-length £/item"
              type={draft.longLengthSurcharge === "" ? "" : "per_order"}
              value={draft.longLengthSurcharge}
              onChange={(_t, v) => setDraft({ ...draft, longLengthSurcharge: v })}
              fixedType="per_order"
            />
            <SurchargeField
              label="Fuel surcharge"
              type={draft.fuelSurchargeType}
              value={draft.fuelSurchargeValue}
              onChange={(t, v) => setDraft({ ...draft, fuelSurchargeType: t, fuelSurchargeValue: v })}
            />
            <SurchargeField
              label="HGV surcharge"
              type={draft.hgvSurchargeType}
              value={draft.hgvSurchargeValue}
              onChange={(t, v) => setDraft({ ...draft, hgvSurchargeType: t, hgvSurchargeValue: v })}
            />
          </div>
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" onClick={() => save.mutate(draft)}>Save</Button>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/60">
            <tr>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Credit email</th>
              <th className="px-3 py-2 text-right">Long-len £/item</th>
              <th className="px-3 py-2 text-right">Fuel</th>
              <th className="px-3 py-2 text-right">HGV</th>
              <th className="px-3 py-2 text-left">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {couriers.map((c) => (
              <CourierRow key={c.id} courier={c} canEdit={isAdmin} onSave={(patch) => save.mutate({ ...patch, id: c.id })} />
            ))}
            {couriers.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-duffells-grey/50">No couriers configured.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Compact two-column surcharge input: type select (% / £-per-order)
 * + numeric value. When `fixedType` is provided the type dropdown is
 * hidden — used for long-length which only has one mode. */
function SurchargeField({ label, type, value, onChange, fixedType }: {
  label: string;
  type: "" | "percent" | "per_order";
  value: string;
  onChange: (type: "" | "percent" | "per_order", value: string) => void;
  fixedType?: "percent" | "per_order";
}) {
  const effectiveType = fixedType ?? type;
  return (
    <label className="text-xs space-y-1">
      <span className="block text-duffells-grey/60">{label}</span>
      <div className="flex gap-1">
        {!fixedType && (
          <select
            className="border rounded px-1 py-1 text-xs"
            value={type}
            onChange={(e) => onChange(e.target.value as any, value)}
          >
            <option value="">—</option>
            <option value="percent">% of invoice</option>
            <option value="per_order">£ per order</option>
          </select>
        )}
        <input
          type="number"
          step="0.01"
          className="border rounded px-2 py-1 w-24 text-right"
          placeholder={effectiveType === "percent" ? "%" : "£"}
          value={value}
          onChange={(e) => onChange((fixedType ? "per_order" : type) as any, e.target.value)}
          disabled={!fixedType && !type}
        />
      </div>
    </label>
  );
}

function CourierRow({ courier, canEdit, onSave }: { courier: Courier; canEdit: boolean; onSave: (p: any) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: courier.name,
    creditEmail: courier.creditEmail || "",
    longLengthSurcharge: courier.longLengthSurcharge == null ? "" : String(courier.longLengthSurcharge),
    fuelSurchargeType: (courier.fuelSurchargeType || "") as "" | "percent" | "per_order",
    fuelSurchargeValue: courier.fuelSurchargeValue == null ? "" : String(courier.fuelSurchargeValue),
    hgvSurchargeType: (courier.hgvSurchargeType || "") as "" | "percent" | "per_order",
    hgvSurchargeValue: courier.hgvSurchargeValue == null ? "" : String(courier.hgvSurchargeValue),
    active: courier.active,
    notes: courier.notes || "",
  });
  if (!editing) {
    return (
      <tr className="border-t border-slate-100">
        <td className="px-3 py-2 font-mono">{courier.code}</td>
        <td className="px-3 py-2">{courier.name}</td>
        <td className="px-3 py-2 text-xs">{courier.creditEmail || <span className="text-amber-600">— not set —</span>}</td>
        <td className="px-3 py-2 text-right">{courier.longLengthSurcharge == null ? <span className="text-duffells-grey/40">—</span> : `£${courier.longLengthSurcharge.toFixed(2)}`}</td>
        <td className="px-3 py-2 text-right text-xs">{formatSurcharge(courier.fuelSurchargeType, courier.fuelSurchargeValue)}</td>
        <td className="px-3 py-2 text-right text-xs">{formatSurcharge(courier.hgvSurchargeType, courier.hgvSurchargeValue)}</td>
        <td className="px-3 py-2">
          {courier.active ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <span className="text-duffells-grey/50 text-xs">No</span>}
        </td>
        <td className="px-3 py-2 text-right">
          {canEdit && <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Button>}
        </td>
      </tr>
    );
  }
  return (
    <>
      <tr className="border-t border-slate-100 bg-amber-50/40">
        <td className="px-3 py-2 font-mono">{courier.code}</td>
        <td className="px-3 py-2"><input className="border rounded px-2 py-1 w-full" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></td>
        <td className="px-3 py-2"><input className="border rounded px-2 py-1 w-full text-xs" value={draft.creditEmail} onChange={(e) => setDraft({ ...draft, creditEmail: e.target.value })} /></td>
        <td className="px-3 py-2"><input type="number" step="0.01" className="border rounded px-2 py-1 w-full text-right text-xs" value={draft.longLengthSurcharge} onChange={(e) => setDraft({ ...draft, longLengthSurcharge: e.target.value })} /></td>
        <td className="px-3 py-2">
          <SurchargeInline
            type={draft.fuelSurchargeType}
            value={draft.fuelSurchargeValue}
            onChange={(t, v) => setDraft({ ...draft, fuelSurchargeType: t, fuelSurchargeValue: v })}
          />
        </td>
        <td className="px-3 py-2">
          <SurchargeInline
            type={draft.hgvSurchargeType}
            value={draft.hgvSurchargeValue}
            onChange={(t, v) => setDraft({ ...draft, hgvSurchargeType: t, hgvSurchargeValue: v })}
          />
        </td>
        <td className="px-3 py-2"><label className="text-xs"><input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} className="mr-1" />Active</label></td>
        <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
          <Button size="sm" onClick={() => { onSave(draft); setEditing(false); }}>Save</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        </td>
      </tr>
    </>
  );
}

function SurchargeInline({ type, value, onChange }: {
  type: "" | "percent" | "per_order";
  value: string;
  onChange: (t: "" | "percent" | "per_order", v: string) => void;
}) {
  return (
    <div className="flex gap-1">
      <select
        className="border rounded px-1 py-1 text-[10px]"
        value={type}
        onChange={(e) => onChange(e.target.value as any, value)}
        title="Surcharge mode"
      >
        <option value="">—</option>
        <option value="percent">%</option>
        <option value="per_order">£/ord</option>
      </select>
      <input
        type="number"
        step="0.01"
        className="border rounded px-1 py-1 w-16 text-right text-xs"
        value={value}
        onChange={(e) => onChange(type, e.target.value)}
        disabled={!type}
      />
    </div>
  );
}
